import React, { useState } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import {
  FormControl,
  TextField,
  FormHelperText,
  Link,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useGetKaapApplications } from '../../hooks/useGetKaapApplications';
import { Application, downloadApplicationDetails } from './download';

export const KaapApplicationSelector = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    schema: { title = '' },
    required,
    rawErrors,
    formData,
    idSchema,
    uiSchema,
  } = props;

  const { value: applications, loading } = useGetKaapApplications();

  const description = (
    <span>
      Select the{' '}
      <Link
        target="_blank"
        style={{ cursor: 'pointer' }}
        onClick={() => downloadApplicationDetails(applications ?? [])}
      >
        application
      </Link>{' '}
      this tenant is for. Beware, this value can only be updated by contacting{' '}
      <Link
        target="_blank"
        href="https://tomtomslack.slack.com/archives/C039NQZUG7K"
      >
        #ccoe-finops
      </Link>
    </span>
  );

  const disabled = Boolean((uiSchema as any)?.['ui:disabled']);
  const hasError = rawErrors?.length > 0;
  const [error, setError] = useState(false);

  const selectedApp =
    (applications ?? []).find(app => app.id === formData) ?? null;
  const wrappedCCName = `(${selectedApp?.ccname})`;

  const onInternalChange = (
    _event: React.ChangeEvent<{}>,
    value: Application | null,
    _reason: any,
    _details?: any,
  ) => {
    onChange(value?.id ?? '');
    if (value) {
      setError(false);
    } else {
      setError(true);
    }
  };

  if (disabled) {
    const displayValue = selectedApp?.name ?? formData ?? '';
    return (
      <FormControl fullWidth>
        <TextField
          id={idSchema?.$id}
          label={title}
          size="small"
          variant="outlined"
          value={`${displayValue} ${selectedApp ? wrappedCCName : ''}`}
          disabled
          inputProps={{ readOnly: true }}
        />
        {description && <FormHelperText>{description}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth required={required} error={hasError && !formData}>
      <Autocomplete
        id={idSchema?.$id}
        size="small"
        value={selectedApp}
        loading={loading}
        options={[...(applications ?? [])].sort((a, b) =>
          a.buname.localeCompare(b.buname),
        )}
        groupBy={option => option.buname}
        getOptionLabel={option => `${option.name} (${option.ccname})`}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={onInternalChange}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            size="small"
            required
            variant="outlined"
            InputProps={params.InputProps}
            error={hasError || error}
          />
        )}
        noOptionsText="No applications found"
        loadingText="Loading applications..."
      />
      {description &&
        (() => {
          let helperText;
          if (hasError) {
            helperText = rawErrors.join(', ');
          } else if (error) {
            helperText = 'Application is required';
          } else {
            helperText = description;
          }
          return (
            <FormHelperText error={hasError || error}>
              {helperText}
            </FormHelperText>
          );
        })()}
    </FormControl>
  );
};
